// src/pages/AccountScreen.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';
import { getLoyaltyTier } from '../lib/currency';
import { fetchUserRewards, checkActiveRewardBlock } from '../lib/discounts';

export default function AccountScreen() {
  const { lang, profile, user, isAdmin, showToast } = useApp();
  const navigate = useNavigate();
  const tier = getLoyaltyTier(profile?.loyalty_points || 0);

  const [rewards, setRewards]             = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [hasActiveBlock, setHasActiveBlock] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetchUserRewards(user.id),
      checkActiveRewardBlock(user.id),
    ]).then(([r, { blocked }]) => {
      setRewards(r);
      setHasActiveBlock(blocked);
      setRewardsLoading(false);
    });
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    showToast('Logged out', 'info');
    navigate('/');
  };

  const activeRewards   = rewards.filter(r => r.active && !r.redeemed);
  const redeemedRewards = rewards.filter(r => r.redeemed);

  const menuItems = [
    { icon: '📦', label: t('my_orders', lang),        path: '/orders' },
    { icon: '❤️', label: t('wishlist', lang),          path: '/wishlist' },
    { icon: '⭐', label: t('loyalty', lang),           path: '/loyalty' },
    { icon: '🎰', label: 'Reward Wheel',               path: '/wheel' },
    { icon: '🌍', label: t('language_currency', lang), path: '/settings' },
    { icon: '🔒', label: t('privacy', lang),           path: '/settings' },
  ];

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 24px' }}>

      {/* Profile card */}
      <div style={{ background: '#111', borderRadius: 20, border: '1px solid rgba(255,255,255,.07)', padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(76,202,116,.15)', border: '2px solid #4cca74', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.4rem', color: '#4cca74', flexShrink: 0 }}>
            {(profile?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '1.1rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {profile?.full_name || user?.email?.split('@')[0]}
            </div>
            <div style={{ fontSize: '.78rem', color: '#4cca74', fontWeight: 600 }}>{tier.label}</div>
            <div style={{ fontSize: '.72rem', color: '#555', marginTop: 1 }}>{user?.email}</div>
          </div>
          <div style={{ flexShrink: 0, background: isAdmin ? 'rgba(76,202,116,.12)' : 'rgba(255,255,255,.04)', border: `1px solid ${isAdmin ? 'rgba(76,202,116,.3)' : 'rgba(255,255,255,.08)'}`, borderRadius: 20, padding: '4px 12px' }}>
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: isAdmin ? '#4cca74' : '#666' }}>
              {profile?.role || 'loading…'}
            </span>
          </div>
        </div>

        {/* Loyalty + spin balance */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: '.72rem', color: '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              Loyalty Points
            </div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.4rem', color: '#4cca74' }}>
              {profile?.loyalty_points || 0}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '.72rem', color: '#9b59b6', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              Wheel Spins
            </div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.4rem', color: '#9b59b6' }}>
              {profile?.spin_balance || 0}
            </div>
          </div>
        </div>

        {/* Loyalty bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: '.72rem', color: '#555' }}>
              {tier.nextTier ? `${tier.nextAt - (profile?.loyalty_points || 0)} pts to ${tier.nextTier}` : 'Elite tier reached!'}
            </span>
          </div>
          <div style={{ background: '#222', borderRadius: 4, height: 5 }}>
            <div style={{ height: '100%', background: '#4cca74', borderRadius: 4, width: tier.nextAt ? `${Math.min(100, ((profile?.loyalty_points || 0) / tier.nextAt) * 100)}%` : '100%', transition: 'width .6s ease' }} />
          </div>
        </div>
      </div>

      {/* Admin shortcut */}
      {isAdmin && (
        <button onClick={() => navigate('/admin')} style={{ width: '100%', background: 'rgba(76,202,116,.08)', border: '1px solid rgba(76,202,116,.25)', borderRadius: 14, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: 16 }}>
          <span style={{ fontSize: '1.2rem' }}>⚙️</span>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#4cca74' }}>Admin Panel</div>
            <div style={{ fontSize: '.75rem', color: '#4cca74', opacity: .7, marginTop: 1 }}>Manage products, orders, blog</div>
          </div>
          <span style={{ marginLeft: 'auto', color: '#4cca74', fontSize: '1.1rem' }}>›</span>
        </button>
      )}

      {/* ── My Rewards ── */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.72rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#9b59b6' }}>
            🎰 My Rewards
          </div>
          {activeRewards.length > 0 && (
            <span style={{ background: '#9b59b6', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: '.65rem', fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>
              {activeRewards.length} active
            </span>
          )}
        </div>

        {/* Wheel-blocked nudge — only shows when active reward blocks the wheel */}
        {hasActiveBlock && (
          <div style={{
            padding: '10px 20px',
            background: 'rgba(155,89,182,.06)',
            borderBottom: '1px solid rgba(155,89,182,.12)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: '.9rem', flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '.75rem', color: '#9b59b6' }}>
                Use your active reward at checkout to unlock the wheel again.
              </span>
            </div>
            <button
              onClick={() => navigate('/checkout')}
              style={{ background: '#9b59b6', border: 'none', borderRadius: 20, padding: '5px 12px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 800, letterSpacing: '.06em', cursor: 'pointer', flexShrink: 0 }}
            >
              Use Now
            </button>
          </div>
        )}

        {rewardsLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : activeRewards.length === 0 && redeemedRewards.length === 0 ? (
          <div style={{ padding: '20px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '.82rem', color: '#444', marginBottom: 10 }}>No rewards yet.</div>
            <button
              onClick={() => navigate('/wheel')}
              style={{ background: 'rgba(155,89,182,.1)', border: '1px solid rgba(155,89,182,.3)', borderRadius: 20, padding: '8px 18px', color: '#9b59b6', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.8rem', fontWeight: 700, cursor: 'pointer' }}
            >
              🎰 Spin the Wheel
            </button>
          </div>
        ) : (
          <>
            {/* Active rewards */}
            {activeRewards.map(r => (
              <div key={r.id} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.9rem', fontWeight: 700, color: '#f4f4f4' }}>
                      {r.label}
                    </span>
                    <span style={{ background: 'rgba(76,202,116,.12)', color: '#4cca74', fontSize: '.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em', textTransform: 'uppercase' }}>
                      Active
                    </span>
                  </div>
                  {r.discount_code && (
                    <div style={{ fontFamily: 'monospace', fontSize: '.78rem', color: '#555', letterSpacing: '.06em', marginTop: 2 }}>{r.discount_code}</div>
                  )}
                  {r.expires_at && (
                    <div style={{ fontSize: '.72rem', color: '#444', marginTop: 2 }}>
                      Expires {new Date(r.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => navigate('/checkout')}
                  style={{ background: '#4cca74', border: 'none', borderRadius: 20, padding: '8px 14px', color: '#000', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.75rem', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Use Now
                </button>
              </div>
            ))}

            {/* Redeemed rewards (collapsed) */}
            {redeemedRewards.length > 0 && (
              <div style={{ padding: '10px 20px' }}>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.68rem', color: '#444', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                  Used ({redeemedRewards.length})
                </div>
                {redeemedRewards.slice(0, 3).map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', color: '#444', marginBottom: 4 }}>
                    <span>{r.label}</span>
                    <span>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : 'Used'}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Menu items */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden', marginBottom: 16 }}>
        {menuItems.map(item => (
          <button key={item.path + item.label} onClick={() => navigate(item.path)}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,.05)', cursor: 'pointer' }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,.03)'}
            onMouseOut={e  => e.currentTarget.style.background = 'none'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
              <span style={{ fontFamily: "'Barlow',sans-serif", fontSize: '.92rem', color: '#ddd' }}>{item.label}</span>
            </div>
            <span style={{ color: '#444', fontSize: '1.1rem' }}>›</span>
          </button>
        ))}
      </div>

      {/* Logout */}
      <button onClick={handleLogout} style={{ width: '100%', background: 'transparent', border: '1px solid rgba(224,80,80,.3)', borderRadius: 25, color: '#e05050', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', padding: '11px 24px', cursor: 'pointer' }}>
        {t('logout', lang)}
      </button>
    </div>
  );
}